import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, ShoppingBag, Share2, Wallet, Megaphone, GraduationCap,
  Fingerprint, Inbox, Shield, LogOut, Menu, X,
} from "lucide-react";
import ChatWidget from "@/components/ChatWidget";
import MobileBottomNav from "@/components/MobileBottomNav";
import useInboxUnreadCount from "@/hooks/useInboxUnreadCount";

// Commerce-first navigation. All investment-era links (Vault, Cash-In Gate,
// Ledger Audit, Guild, Pulse Board) have been retired in favor of the CaaS
// partner journey: dashboard → shop → share → earn → withdraw.
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/shop", label: "Product Catalog", icon: ShoppingBag, testid: "nav-shop" },
  { to: "/rewards", label: "Partner Rewards", icon: Share2, testid: "nav-rewards" },
  { to: "/earnings", label: "Commission Wallet", icon: Wallet, testid: "nav-earnings" },
  { to: "/marketing", label: "Marketing Center", icon: Megaphone, testid: "nav-marketing" },
  { to: "/kyc", label: "Partner Verification", icon: Fingerprint, testid: "nav-kyc" },
  { to: "/inbox", label: "Message Support", adminLabel: "Support Inbox", icon: Inbox, testid: "nav-inbox" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unreadInbox = useInboxUnreadCount();

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex zfd-canvas text-white">
      {/* Sidebar */}
      <aside className={`${open ? "fixed inset-y-0 left-0 z-40" : "hidden"} md:flex md:relative md:flex-col w-72 bg-[#0F2042]/80 backdrop-blur-xl border-r border-white/5 px-5 py-6`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#FBBF24] font-bold" data-testid="brand-tagline">Zenith Flow</div>
            <div className="font-display text-2xl font-black tracking-tight">DYNAMICS</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">Commerce-as-a-Service</div>
          </div>
          <button className="md:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar"><X size={20} /></button>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map((n) => {
            const isAdminSide =
              user?.role === "admin" ||
              (user?.role === "sub_admin" && (user?.admin_permissions || []).includes("inbox"));
            const label = isAdminSide && n.adminLabel ? n.adminLabel : n.label;
            const isInbox = n.testid === "nav-inbox";
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-[#1E3A8A] text-white shadow-[0_0_18px_rgba(30,58,138,0.45)]"
                      : "text-[#F3F4F6]/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <n.icon size={18} />
                <span className="flex-1">{label}</span>
                {isInbox && unreadInbox > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold font-mono-num shadow-[0_0_10px_rgba(220,38,38,0.55)] animate-pulse"
                    data-testid="nav-inbox-unread-badge"
                    aria-label={`${unreadInbox} unread inbox ${unreadInbox === 1 ? "message" : "messages"}`}
                  >
                    {unreadInbox > 99 ? "99+" : unreadInbox}
                  </span>
                )}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all border-t border-white/5 mt-4 pt-4 ${
                  isActive ? "bg-[#DC2626] text-white" : "text-[#FBBF24] hover:bg-white/5"
                }`
              }
            >
              <Shield size={18} />
              <span>Admin Operations</span>
            </NavLink>
          )}
        </nav>
        <div className="mt-6 pt-6 border-t border-white/5">
          <div className="text-xs text-white/50 mb-1">Signed in as</div>
          <div className="font-medium text-sm truncate" data-testid="layout-user-name">{user?.full_name}</div>
          <div className="text-xs text-white/40 truncate">{user?.email}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 hover:bg-[#DC2626] hover:text-white text-white/80 transition-colors text-sm font-semibold"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#0F2042]/60">
          <button onClick={() => setOpen(true)} data-testid="open-sidebar"><Menu /></button>
          <div className="font-display font-black tracking-tight">ZFD</div>
          <div />
        </header>
        <main className="px-5 md:px-10 py-8 md:py-10 pb-24 md:pb-10 max-w-[1400px] mx-auto">
          {children}
        </main>
      </div>
      <MobileBottomNav />
      <ChatWidget />
    </div>
  );
}
